﻿import { ref, computed } from "vue";
import { useCordovaPlugin } from "./useCordovaPlugin.js";
import { f7 } from "framework7-vue";
import i18n from '../i18n.js';

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}

function tOrFallback(key, fallback) {
  try {
    const translated = i18n.global.t(key);
    return translated === key ? fallback : translated;
  } catch {
    return fallback;
  }
}

export function useContactPicker() {
  const {
    selectedContacts,
    canUseContacts,
    selectContact,
    removeContact,
    clearSelectedContacts,
    getSelectedContactsForInvitation,
  } = useCordovaPlugin();

  const showPicker = ref(false);
  const selectedContactData = ref([]);

  const hasMultipleContacts = computed(() => selectedContacts.value.length > 1);
  const formattedContacts = computed(() => getSelectedContactsForInvitation());
  const count = computed(() => selectedContacts.value.length);

  /**
   * Opens the platform contact picker (native plugin or web Contact Picker
   * API â€” the latter requires a user gesture, so this must be called
   * directly from a click handler), then shows a review popup built with
   * plain DOM (Framework7 renders it; no Vue directives in raw content).
   * Resolves with the final normalized selection.
   */
  async function openContactPicker(options = {}) {
    const {
      multiple = true,
      title = i18n.global.t('contactPicker.selectContacts'),
      confirmText = i18n.global.t('contactPicker.ok'),
      cancelText = i18n.global.t('contactPicker.cancel'),
    } = options;

    if (!canUseContacts.value) {
      f7.toast.show({
        text: tOrFallback('contactPicker.notAvailable', 'Contacts are not available on this device'),
        closeTimeout: 2500,
      });
      return [];
    }

    // Fresh selection on every open
    clearSelectedContacts();

    // Call the picker directly while we still have the user gesture
    const picked = await selectContact({ multiple });
    if (!picked || picked.length === 0) {
      return [];
    }

    return new Promise((resolve) => {
      const popup = f7.popup.create({
        content: `
          <div class="popup contact-picker-popup">
            <div class="view view-root">
              <div class="page">
                <div class="navbar">
                  <div class="navbar-bg"></div>
                  <div class="navbar-inner">
                    <div class="title">${escapeHtml(title)}</div>
                    <div class="right">
                      <a href="#" class="link popup-close">${escapeHtml(cancelText)}</a>
                    </div>
                  </div>
                </div>
                <div class="page-content">
                  <div class="block block-strong no-padding contact-picker-head">
                    <p class="contact-picker-count">
                      ${escapeHtml(tOrFallback('contactPicker.selectedContacts', 'Selected contacts'))}
                      (<span class="cp-count">0</span>)
                    </p>
                    <div class="display-flex gap-2">
                      <a href="#" class="button button-outline button-small cp-clear">
                        ${escapeHtml(i18n.global.t('contactPicker.clear'))}
                      </a>
                      <a href="#" class="button button-fill button-small cp-confirm">
                        ${escapeHtml(confirmText)}
                      </a>
                    </div>
                  </div>
                  <div class="list outline-none">
                    <ul class="cp-list"></ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `,
        closeByBackdropClick: !multiple,
        on: {
          popupClosed: () => {
            showPicker.value = false;
            const finalSelection = getSelectedContactsForInvitation();
            selectedContactData.value = finalSelection;
            resolve(finalSelection);
          },
        },
      });

      popup.open();
      showPicker.value = true;

      const $el = popup.$el;
      const listEl = $el.find('.cp-list')[0];
      const countEl = $el.find('.cp-count')[0];

      const renderList = () => {
        const items = getSelectedContactsForInvitation();
        countEl.textContent = items.length;
        if (items.length === 0) {
          listEl.innerHTML = `
            <li class="item-content">
              <div class="item-inner">
                <div class="item-title">${escapeHtml(tOrFallback('contactPicker.noContacts', 'No contacts selected'))}</div>
              </div>
            </li>`;
          return;
        }
        listEl.innerHTML = items
          .map((contact) => `
            <li class="item-content" data-id="${escapeHtml(contact.id || '')}">
              <div class="item-media"><i class="f7-icons">person_circle</i></div>
              <div class="item-inner">
                <div class="item-title">${escapeHtml(contact.name)}</div>
                <div class="item-text">${escapeHtml(contact.phonesSingle || contact.emailsSingle || '')}</div>
              </div>
              <div class="item-after">
                <a href="#" class="link cp-remove" data-id="${escapeHtml(contact.id || '')}">
                  <i class="f7-icons color-red">trash</i>
                </a>
              </div>
            </li>`)
          .join('');
      };

      $el.on('click', '.cp-remove', (e) => {
        e.preventDefault();
        const id = e.currentTarget?.getAttribute('data-id');
        if (id) removeContact({ id });
        renderList();
      });

      $el.find('.cp-clear').on('click', (e) => {
        e.preventDefault();
        clearSelectedContacts();
        renderList();
      });

      $el.find('.cp-confirm').on('click', (e) => {
        e.preventDefault();
        popup.close();
      });

      renderList();
    });
  }

  function removeSelectedContact(index) {
    selectedContacts.value.splice(index, 1);
    if (selectedContacts.value.length === 0) {
      selectedContactData.value = [];
    }
  }

  function clearSelection() {
    clearSelectedContacts();
  }

  return {
    showPicker,
    hasMultipleContacts,
    formattedContacts,
    count,
    openContactPicker,
    removeSelectedContact,
    clearSelection,
  };
}
